import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Bot, CheckCircle2, Coins, Folder, Mic, Users } from 'lucide-react'
import { toast } from 'sonner'

import { supabase, getCurrentSession, signOut } from '../lib/supabase'
import { daysAgoKey, emptyTotals, fetchTeamAgentUsageDaily, sumByProject, toDateKey, type AgentUsageDailyRow } from '../lib/agentUsage'
import { LIST_PRICE_LABEL, TokenBreakdown, UsageCostText } from '../components/UsageCost'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Card, CardContent } from '../components/ui/card'
import { Avatar, AvatarFallback } from '../components/ui/avatar'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'

interface ProjectDetailsProps {
  teamId: string
  projectId: string
}

interface Project {
  id: string
  name: string
  description: string | null
  created_at: string
}

interface AgentRow {
  id: string
  name: string
  project_id: string | null
}

interface TeamMember {
  auth_user_id: string | null
  user_name: string | null
  user_email: string | null
  avatar_url: string | null
}

interface Pod {
  id: string
  name: string
}

const PROJECT_AVATAR_PALETTE = [
  'bg-pink-500',
  'bg-blue-500',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-amber-500',
  'bg-cyan-600',
]

function paletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return PROJECT_AVATAR_PALETTE[hash % PROJECT_AVATAR_PALETTE.length]
}

function initialsOf(name: string): string {
  return name
    .split(/[\s-]+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'
}

function memberName(member: TeamMember): string {
  return member.user_name || member.user_email?.split('@')[0] || 'Team member'
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ teamId, projectId }) => {
  const navigate = useNavigate()
  const [teamName, setTeamName] = useState<string>()
  const [project, setProject] = useState<Project | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [pods, setPods] = useState<Pod[]>([])
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [usageRows, setUsageRows] = useState<AgentUsageDailyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const session = await getCurrentSession()
        const [teamRes, projectRes, projectMembersRes, projectPodsRes, agentsRes, membersRes, usage] = await Promise.all([
          supabase.from('teams').select('name').eq('id', teamId).single(),
          (supabase.from as any)('projects')
            .select('id, name, description, created_at')
            .eq('id', projectId)
            .eq('team_id', teamId)
            .is('deleted_at', null)
            .maybeSingle(),
          (supabase.from as any)('project_members').select('auth_user_id').eq('project_id', projectId),
          (supabase.from as any)('project_pods').select('pod_id').eq('project_id', projectId),
          (supabase.from as any)('agents_team_visible').select('id, name, project_id').eq('team_id', teamId),
          session
            ? supabase.functions.invoke('get-all-team-users', {
                body: { team_id: teamId },
                headers: { Authorization: `Bearer ${session.access_token}` },
              })
            : Promise.resolve({ data: { users: [] }, error: null }),
          fetchTeamAgentUsageDaily(teamId, daysAgoKey(6), toDateKey(new Date())),
        ])

        if (teamRes.error) throw teamRes.error
        if (projectRes.error) throw projectRes.error
        if (projectMembersRes.error) throw projectMembersRes.error
        if (projectPodsRes.error) throw projectPodsRes.error
        if (agentsRes.error) throw agentsRes.error
        if (membersRes.error) throw membersRes.error

        if (cancelled) return

        const projectRow = projectRes.data as Project | null
        if (!projectRow) {
          setError('This project could not be found or you do not have access to it.')
          return
        }

        const memberIds = new Set(
          ((projectMembersRes.data ?? []) as { auth_user_id: string }[]).map((row) => row.auth_user_id)
        )
        const teamUsers = (membersRes.data?.users ?? []) as TeamMember[]
        const projectMembers = teamUsers.filter((member) => member.auth_user_id && memberIds.has(member.auth_user_id))
        const podIds = new Set(((projectPodsRes.data ?? []) as { pod_id: string }[]).map((row) => row.pod_id))

        let podRows: Pod[] = []
        if (podIds.size > 0) {
          const { data, error: podError } = await (supabase.from as any)('pods')
            .select('id, name')
            .in('id', [...podIds])
          if (podError) throw podError
          podRows = (data ?? []) as Pod[]
        }

        setTeamName(teamRes.data?.name ?? undefined)
        setProject(projectRow)
        setMembers(projectMembers)
        setPods(podRows)
        setAgents((agentsRes.data ?? []) as AgentRow[])
        setUsageRows(usage)
      } catch (loadError) {
        console.error('Failed to load project details:', loadError)
        if (!cancelled) setError('Could not load this project right now.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [projectId, teamId])

  const projectAgents = useMemo(
    () => agents.filter((agent) => agent.project_id === projectId),
    [agents, projectId]
  )
  const projectSpend = useMemo(() => sumByProject(usageRows).get(projectId) ?? emptyTotals(), [projectId, usageRows])

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const goToProjects = () => navigate(`/dashboard/team/${teamId}/projects`)

  return (
    <SidebarProvider>
      <AppSidebar activeSection="projects" onNavigate={goToProjects} teamId={teamId} />
      <SidebarInset>
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Projects" />
        <main className="min-h-[calc(100vh-57px)] space-y-6 bg-background p-4 sm:p-6 lg:p-8">
          <Button variant="ghost" className="-ml-2 gap-2 text-muted-foreground" onClick={goToProjects}>
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Button>

          {loading ? (
            <div className="space-y-6">
              <Skeleton className="h-12 w-72" />
              <div className="grid grid-cols-1 divide-y divide-border overflow-hidden border border-border bg-background shadow-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
                {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-32 rounded-none" />)}
              </div>
              <Skeleton className="h-64 w-full" />
            </div>
          ) : error || !project ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                <div>
                  <h1 className="font-semibold text-foreground">Project unavailable</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{error ?? 'This project is no longer available.'}</p>
                </div>
                <Button variant="outline" onClick={goToProjects}>Return to projects</Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-14 w-14 rounded-md">
                    <AvatarFallback className={`rounded-md text-lg font-semibold text-white ${paletteFor(project.id)}`}>
                      {project.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">{project.name}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {project.description || teamName || 'Project workspace'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={() => toast.info('The project view currently covers the last 7 days.')}>Last 7 days</Button>
                  <Button className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate('/voice-record')}>
                    <Mic className="h-4 w-4" />
                    Record update
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 divide-y divide-border overflow-hidden border border-border bg-background shadow-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
                <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Standups" value="—" helper="No project-scoped check-ins yet" />
                <StatCard icon={<Users className="h-4 w-4" />} label="People & connectors" value={(members.length + projectAgents.length).toString()} helper={`${members.length} people · ${projectAgents.length} connectors`} />
                <StatCard icon={<Coins className="h-4 w-4" />} label="Token spend" value={projectSpend.eventCount ? <UsageCostText totals={projectSpend} prefix="" /> : '—'} helper={projectSpend.eventCount ? `${projectSpend.totalTokens.toLocaleString()} tokens · ${LIST_PRICE_LABEL.toLowerCase()}` : 'No usage in the last 7 days'} />
                <StatCard icon={<AlertCircle className="h-4 w-4" />} label="Blockers" value="—" helper="No project-scoped blockers yet" />
              </div>

              <Card className="bg-background">
                <CardContent className="space-y-6 p-5 sm:p-6">
                  <div className="flex flex-col gap-1 border-b border-border pb-4">
                    <h2 className="text-base font-semibold text-foreground">Activities in this project</h2>
                    <p className="text-sm text-muted-foreground">What&apos;s been shipped, said, and done here — as it happens.</p>
                  </div>

                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-6 py-14 text-center">
                    <Folder className="h-8 w-8 text-muted-foreground/60" />
                    <p className="font-medium text-foreground">No project activity yet</p>
                    <p className="max-w-md text-sm text-muted-foreground">
                      Activity will appear here when project-scoped updates are available.
                    </p>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-3">
                    <DetailList title="People" icon={<Users className="h-4 w-4" />} empty="No people assigned yet." items={members.map((member) => ({ id: member.auth_user_id ?? member.user_email ?? 'member', label: memberName(member), avatar: member.avatar_url }))} />
                    <DetailList title="Connectors" icon={<Bot className="h-4 w-4" />} empty="No connectors assigned yet." items={projectAgents.map((agent) => ({ id: agent.id, label: agent.name }))} />
                    <DetailList title="Teams" icon={<Folder className="h-4 w-4" />} empty="No teams assigned yet." items={pods.map((pod) => ({ id: pod.id, label: pod.name }))} />
                  </div>

                  {projectSpend.eventCount > 0 && <TokenBreakdown totals={projectSpend} className="border-t border-border pt-4" />}
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; helper: string }> = ({ icon, label, value, helper }) => (
  <div className="bg-background p-5">
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{icon}{label}</div>
    <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
    <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
  </div>
)

const DetailList: React.FC<{ title: string; icon: React.ReactNode; empty: string; items: { id: string; label: string; avatar?: string | null }[] }> = ({ title, icon, empty, items }) => (
  <div className="space-y-3 border border-border bg-background p-4">
    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">{icon}{title}</div>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">{empty}</p>
    ) : (
      <div className="space-y-2">
        {items.slice(0, 5).map((item) => (
          <div key={item.id} className="flex items-center gap-2 text-sm text-foreground">
            <Avatar className="h-7 w-7">
              {item.avatar && <img src={item.avatar} alt="" className="h-full w-full object-cover" />}
              <AvatarFallback className={`text-[10px] font-semibold text-white ${paletteFor(item.id)}`}>
                {initialsOf(item.label)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{item.label}</span>
          </div>
        ))}
        {items.length > 5 && <p className="text-xs text-muted-foreground">+ {items.length - 5} more</p>}
      </div>
    )}
  </div>
)

export default ProjectDetails
